import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, message, Alert, Space } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { authApi } from '../../services/api';
import { RegisterRequest } from '../../types';

const { Title, Text } = Typography;

interface RegisterForm {
  name: string;
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
}

const Register: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const handleRegister = async (values: RegisterForm) => {
    try {
      setLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      const registerData: RegisterRequest = {
        name: values.name,
        email: values.email,
        username: values.username,
        password: values.password,
      };

      const response = await authApi.register(registerData);
      
      // 注册成功
      setSuccessMessage(`注册成功！欢迎 ${response.data.name}`);
      
      // 清空表单
      form.resetFields();
      
      // 3秒后跳转到登录页面
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: '注册成功，请使用您的用户名和密码登录',
            username: values.username
          }
        });
      }, 3000);
      
    } catch (error: any) {
      console.error('Register error:', error);
      
      // 获取具体的错误信息
      let errorMsg = '';
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.response?.status === 400) {
        errorMsg = error.response?.data?.error || '请求参数错误，请检查输入内容';
      } else if (error.response?.status === 500) {
        errorMsg = '服务器内部错误，请稍后重试';
      } else {
        errorMsg = '网络连接失败，请检查网络后重试';
      }
      
      setErrorMessage(errorMsg);
      
      // 开发环境下打印详细错误信息
      if (process.env.NODE_ENV === 'development') {
        console.group('注册错误详情:');
        console.log('状态码:', error.response?.status);
        console.log('错误信息:', error.response?.data);
        console.log('完整错误:', error);
        console.groupEnd();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <Card style={{ width: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2}>APILink 用户注册</Title>
          <Text type="secondary">创建您的账户</Text>
        </div>

        <Alert
          message="用户注册"
          description="请填写以下信息来创建您的新账户。"
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        {/* 错误信息显示 */}
        {errorMessage && (
          <Alert
            message="注册失败"
            description={errorMessage}
            type="error"
            showIcon
            closable
            onClose={() => setErrorMessage('')}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* 成功信息显示 */}
        {successMessage && (
          <Alert
            message="注册成功"
            description={successMessage}
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={form}
          name="register"
          onFinish={handleRegister}
          autoComplete="off"
          size="large"
          layout="vertical"
        >
          <Form.Item
            label="用户名"
            name="name"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 2, message: '用户名至少2个字符' },
              { max: 50, message: '用户名最多50个字符' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="请输入用户名"
              autoComplete="name"
              onChange={() => setErrorMessage('')}
            />
          </Form.Item>

          <Form.Item
            label="邮箱地址"
            name="email"
            rules={[
              { required: true, message: '请输入邮箱地址' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="请输入邮箱地址"
              autoComplete="email"
              onChange={() => setErrorMessage('')}
            />
          </Form.Item>

          <Form.Item
            label="登录用户名"
            name="username"
            rules={[
              { required: true, message: '请输入登录用户名' },
              { min: 3, message: '登录用户名至少3个字符' },
              { max: 50, message: '登录用户名最多50个字符' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: '登录用户名只能包含字母、数字和下划线' }
            ]}
          >
            <Input
              prefix={<EditOutlined />}
              placeholder="请输入登录用户名"
              autoComplete="username"
              onChange={() => setErrorMessage('')}
            />
          </Form.Item>

          <Form.Item
            label="密码"
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6个字符' },
              { pattern: /^(?=.*[a-zA-Z])(?=.*\d)/, message: '密码必须包含至少一个字母和一个数字' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请输入密码"
              autoComplete="new-password"
              onChange={() => setErrorMessage('')}
            />
          </Form.Item>

          <Form.Item
            label="确认密码"
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请再次输入密码"
              autoComplete="new-password"
              onChange={() => setErrorMessage('')}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ width: '100%', height: 45 }}
              disabled={!!successMessage}
            >
              {loading ? '注册中...' : '注册'}
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            <Space>
              <Text type="secondary">已有账户？</Text>
              <Link to="/login">立即登录</Link>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default Register; 